"use client";

import { useState, useEffect, useCallback } from "react";

const STORAGE_KEY = "saysay-word-state";

export type WordState = { known?: boolean; tough?: boolean; liked?: boolean };
export type WordStateMap = Record<string, WordState>;

function loadState(): WordStateMap {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {
    // ignore
  }
  return {};
}

function saveState(state: WordStateMap) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore
  }
}

export function useWordState() {
  const [state, setState] = useState<WordStateMap>({});

  // Load from localStorage on mount
  useEffect(() => {
    setState(loadState());
  }, []);

  // Save to localStorage on change
  useEffect(() => {
    saveState(state);
  }, [state]);

  const toggle = useCallback((baseForm: string, field: keyof WordState) => {
    const key = baseForm.toLowerCase();
    setState((prev) => {
      const current = prev[key] || {};
      const updated = { ...current, [field]: !current[field] };
      // Clean up falsy values
      if (!updated.known) delete updated.known;
      if (!updated.tough) delete updated.tough;
      if (!updated.liked) delete updated.liked;
      const next = { ...prev };
      if (Object.keys(updated).length === 0) {
        delete next[key];
      } else {
        next[key] = updated;
      }
      return next;
    });
  }, []);

  return {
    state,
    isKnown: useCallback((baseForm: string) => !!state[baseForm.toLowerCase()]?.known, [state]),
    isTough: useCallback((baseForm: string) => !!state[baseForm.toLowerCase()]?.tough, [state]),
    isLiked: useCallback((baseForm: string) => !!state[baseForm.toLowerCase()]?.liked, [state]),
    toggleKnown: useCallback((baseForm: string) => toggle(baseForm, "known"), [toggle]),
    toggleTough: useCallback((baseForm: string) => toggle(baseForm, "tough"), [toggle]),
    toggleLiked: useCallback((baseForm: string) => toggle(baseForm, "liked"), [toggle]),
  };
}
